import React, { useCallback, useEffect, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Header from '../Header';
import AdminNav from './AdminNav';
import { useAuth } from '../../context/AuthContext';
import { ApiError } from '../../services/online/apiClient';

const PAGE_SIZE = 20;

export default function AdminErrorsScreen() {
    const { authFetch } = useAuth();
    const [roomId, setRoomId] = useState('');
    const [source, setSource] = useState('');
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(
        async (offset = 0) => {
            setError(null);
            setIsLoading(true);
            try {
                const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
                if (roomId.trim()) params.set('roomId', roomId.trim());
                if (source.trim()) params.set('source', source.trim());
                const result = await authFetch(`/admin/errors?${params.toString()}`);
                if (offset === 0) setItems(result.items);
                else setItems((prev) => [...prev, ...result.items]);
                setTotal(result.total);
            } catch (err) {
                setError(err instanceof ApiError ? err.message : 'No se pudo cargar la lista de errores.');
            } finally {
                setIsLoading(false);
            }
        },
        [authFetch, roomId, source]
    );

    useEffect(() => {
        const handle = setTimeout(() => load(0), 300);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roomId, source]);

    return (
        <>
            <Header title="Errores de servidor" showSettings={false} />
            <AdminNav />
            <Container maxWidth="md" sx={{ mt: 3, mb: 4 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                    <TextField label="Filtrar por roomId" value={roomId} onChange={(e) => setRoomId(e.target.value)} size="small" fullWidth />
                    <TextField label="Filtrar por origen" value={source} onChange={(e) => setSource(e.target.value)} size="small" fullWidth />
                </Stack>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <Stack spacing={1}>
                    {items.map((e) => (
                        <Accordion key={e.id} disableGutters>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Stack sx={{ width: '100%' }} direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="body2" noWrap>{e.message || e.source}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {new Date(e.createdAt).toLocaleString()}
                                        </Typography>
                                    </Box>
                                    <Chip size="small" label={e.source} />
                                </Stack>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem', overflowX: 'auto' }}>
                                    {JSON.stringify(e, null, 2)}
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                    {!isLoading && items.length === 0 && (
                        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>Sin errores registrados.</Typography>
                    )}
                </Stack>

                {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress />
                    </Box>
                )}

                {!isLoading && items.length < total && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Button onClick={() => load(items.length)}>Cargar más ({items.length}/{total})</Button>
                    </Box>
                )}
            </Container>
        </>
    );
}
